import React, { useState, useEffect, useRef} from 'react';
import {adminEventsApi, adminMediaApi, eventPromoCodesApi } from '../../lib/api';
import { formatTime, toDateInputFormat} from '../../lib/dateUtils';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const EventManagement = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [sortOrder, setSortOrder] = useState('asc');
  const [formData, setFormData] = useState({
    golf_course: '',
    township: '',
    state: '',
    zipcode:'',
    date: '',
    start_time: '',
    guest_price: '',
    member_price: '',
    capacity:'',
    image_url: '',
    event_type: 'regular',
    gala_guest_price: ''
  });

  // Promo code state
  const [promoCodes, setPromoCodes] = useState([]);
  const [showPromoForm, setShowPromoForm] = useState(false);
  const [promoFormData, setPromoFormData] = useState({
    code: '',
    discount_type: 'member_price',
    discount_value: '',
    event_id: '',
    max_uses: 1,
    expires_at: '',
  });

  const formRef = useRef(null);

  useEffect(() => {
    fetchEvents();
    fetchPromoCodes();
  }, []);

  // ✅ Helper to get full image URL with cache-busting for uploaded files
  const getFullImageUrl = (url, bust = false) => {
    if (!url) return '';
    let fullUrl;
    if (url.startsWith('http://') || url.startsWith('https://')) fullUrl = url;
    else if (url.startsWith('/')) fullUrl = `${API_URL}${url}`;
    else fullUrl = `${API_URL}/${url}`;
    // Add cache-busting for locally uploaded images so new uploads always show fresh
    if (bust && (url.startsWith('/uploads/') || url.includes('/uploads/'))) {
      fullUrl += `?v=${Date.now()}`;
    }
    return fullUrl;
  };

  const fetchPromoCodes = async () => {
    try {
      const data = await eventPromoCodesApi.getAll();
      setPromoCodes(data);
    } catch (err) {
      console.error('Failed to load promo codes', err);
    }
  };

  const handleCreatePromoCode = async (e) => {
    e.preventDefault();
    try {
      setError(null);
      const payload = {
        code: promoFormData.code || undefined,
        discount_type: promoFormData.discount_type,
        discount_value: promoFormData.discount_type === 'percent' ? parseFloat(promoFormData.discount_value) : undefined,
        event_id: promoFormData.event_id ? parseInt(promoFormData.event_id) : undefined,
        max_uses: parseInt(promoFormData.max_uses) || 1,
        expires_at: promoFormData.expires_at || undefined,
      };
      await eventPromoCodesApi.create(payload);
      setSuccess('Promo code created successfully');
      setShowPromoForm(false);
      setPromoFormData({ code: '', discount_type: 'member_price', discount_value: '', event_id: '', max_uses: 1, expires_at: '' });
      await fetchPromoCodes();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.message || 'Failed to create promo code');
    }
  };

  const handleDeletePromoCode = async (codeId) => {
    if (!window.confirm('Are you sure you want to delete this promo code?')) return;
    try {
      await eventPromoCodesApi.delete(codeId);
      setPromoCodes(prev => prev.filter(c => c.id !== codeId));
      setSuccess('Promo code deleted');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.message || 'Failed to delete promo code');
    }
  };

  const fetchEvents = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await adminEventsApi.getAll('date', sortOrder);
      setEvents(data);
    } catch (err) {
      setError(err.message || 'Failed to load events');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleCapacityChange = (e) => {
    const value = e.target.value;
    
    // Only allow digits
    if (value === '' || /^\d+$/.test(value)) {
      setFormData({ ...formData, capacity: value });
    }
  };

  // ✅ Updated image upload to use proper file upload
  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError('Please select a valid image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setError('Image size must be less than 5MB');
      return;
    }

    try {
      setUploadingImage(true);
      setError(null);

      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const response = await adminMediaApi.uploadImage(formDataUpload);
      setFormData(prev => ({ ...prev, image_url: response.url }));
      setSuccess('Image uploaded successfully');
      setTimeout(() => setSuccess(null), 2000);
    } catch (err) {
      setError(err.message || 'Failed to upload image');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleRemoveImage = () => {
    setFormData({ ...formData, image_url: '' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setError(null);
      setSuccess(null);

      // Validate zipcode
      const zipcodeRegex = /^\d{5}$/;
      if (!zipcodeRegex.test(formData.zipcode)) {
        setError('Zipcode must be exactly 5 digits');
        return;
      }

      const dataToSend = {
        golf_course: formData.golf_course,
        township: formData.township,
        state: formData.state,
        zipcode: formData.zipcode,
        date: formData.date,
        start_time: formData.start_time,
        guest_price: parseFloat(formData.guest_price),
        member_price: parseFloat(formData.member_price),
        capacity: parseInt(formData.capacity),
        image_url: formData.image_url || null,
        event_type: formData.event_type,
        gala_guest_price: formData.event_type === 'championship' && formData.gala_guest_price
          ? parseFloat(formData.gala_guest_price)
          : null,
      };

      

      if (editingEvent) {
        await adminEventsApi.update(editingEvent.id, dataToSend);
        
        setSuccess('Event updated successfully');
      } else {
        await adminEventsApi.create(dataToSend);
        setSuccess('Event created successfully');
      }

      await fetchEvents();
      handleCancel();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.message || 'Failed to save event');
    }
  };

  const handleEdit = (event) => {
    setEditingEvent(event);
    setFormData({
      golf_course: event.golf_course,
      township: event.township,
      state: event.state,
      zipcode: event.zipcode,
      date: toDateInputFormat(event.date),
      start_time: event.start_time,
      guest_price: event.guest_price,
      member_price: event.member_price,
      capacity: event.capacity,
      image_url: event.image_url || '',
      event_type: event.event_type || 'regular',
      gala_guest_price: event.gala_guest_price != null ? event.gala_guest_price : ''
    });
    setShowForm(true);

    setTimeout(() => {
      formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  const handleDelete = async (eventId, eventName) => {
    if (!window.confirm(`Are you sure you want to delete "${eventName}"?`)) {
      return;
    }

    try {
      setError(null);
      await adminEventsApi.delete(eventId);
      setEvents(events.filter(e => e.id !== eventId));
      setSuccess('Event deleted successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.message || 'Failed to delete event');
    }
  };

  const handleToggleRegistration = async (event) => {
    const currentState = event.registration_open !== false; // treat undefined/null as true
    const newState = !currentState;
    // Optimistic update
    setEvents(prev =>
      prev.map(e => e.id === event.id ? { ...e, registration_open: newState } : e)
    );
    try {
      await adminEventsApi.toggleRegistration(event.id, newState);
      setSuccess(`Registration ${newState ? 'enabled' : 'disabled'} for ${event.golf_course}`);
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      // Revert on failure
      setEvents(prev =>
        prev.map(e => e.id === event.id ? { ...e, registration_open: !newState } : e)
      );
      setError('Failed to update registration status');
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingEvent(null);
    setFormData({
      golf_course: '',
      township: '',
      state: '',
      zipcode: '',
      date: '',
      start_time: '',
      guest_price: '',
      member_price: '',
      capacity: '',
      image_url: '',
      event_type: 'regular',
      gala_guest_price: ''
    });
  };

  const handleSortToggle = async () => {
    const newOrder = sortOrder === 'asc' ? 'desc' : 'asc';
    setSortOrder(newOrder);
    
    try {
      const data = await adminEventsApi.getAll('date', newOrder);
      setEvents(data);
    } catch (err) {
      setError(err.message || 'Failed to sort events');
    }
  };

  if (loading) {
    return <div className="loading">Loading events...</div>;
  }

  return (
    <div className="event-management">
      <div className="section-header">
        <h2 className="admin-section-title">Event Management</h2>
        <div className="header-actions">
          <button onClick={handleSortToggle} className="btn-secondary">
            Sort by Date: {sortOrder === 'asc' ? '↑ Oldest First' : '↓ Newest First'}
          </button>
          {!showForm && (
            <button onClick={() => { setFormData(prev => ({ ...prev, event_type: 'regular' })); setShowForm(true); }} className="btn-primary">
              + Add New Event
            </button>
          )}
        </div>
      </div>

      {error && <div className="error">{error}</div>}
      {success && <div className="success">{success}</div>}

      {showForm && (
        <div className="event-form-card" ref={formRef}>
          <h3>{editingEvent ? 'Edit Event' : 'Create New Event'}</h3>
          <form onSubmit={handleSubmit} className="admin-form">
            <div className="form-row">
              <div className="form-group">
                <label>Golf Course *</label>
                <input
                  type="text"
                  name="golf_course"
                  value={formData.golf_course}
                  onChange={handleInputChange}
                  required
                  placeholder="e.g., Pebble Beach Golf Links"
                />
              </div>
              <div className="form-group">
                <label>Township *</label>
                <input
                  type="text"
                  name="township"
                  value={formData.township}
                  onChange={handleInputChange}
                  required
                  placeholder="e.g., Pebble Beach"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>State *</label>
                <input
                  type="text"
                  name="state"
                  value={formData.state}
                  onChange={handleInputChange}
                  required
                  maxLength="2"
                  placeholder="e.g., CA"
                />
              </div>
              <div className="form-group">
                <label>Zipcode *</label>
                <input
                  type="text"
                  name="zipcode"
                  value={formData.zipcode}
                  onChange={handleInputChange}
                  required
                  pattern="\d{5}"
                  placeholder="e.g., 93953"
                />
                <small className="help-text">Must be 5 digits</small>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Date *</label>
                <input
                  type="date"
                  name="date"
                  value={formData.date}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>Start Time *</label>
                <input
                  type="time"
                  name="start_time"
                  value={formData.start_time}
                  onChange={handleInputChange}
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Guest Price *</label>
                <input
                  type="number"
                  name="guest_price"
                  value={formData.guest_price}
                  onChange={handleInputChange}
                  required
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                />
              </div>
              <div className="form-group">
                <label>Member Price *</label>
                <input
                  type="number"
                  name="member_price"
                  value={formData.member_price}
                  onChange={handleInputChange}
                  required
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                />
              </div>
            </div>

            {formData.event_type === 'championship' && (
              <div className="form-group">
                <label>Gala Guest Price</label>
                <input
                  type="number"
                  name="gala_guest_price"
                  value={formData.gala_guest_price}
                  onChange={handleInputChange}
                  step="0.01"
                  min="0"
                  placeholder="75.00"
                />
              </div>
            )}

            <div className="form-group">
              <label>Capacity *</label>
              <input
                type="text"
                name="capacity"
                value={formData.capacity}
                onChange={handleCapacityChange}
                required
                placeholder="e.g., 50"
                inputMode="numeric"
              />
            </div>

            <div className="form-group">
              <label>Event Image</label>
              <input
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                disabled={uploadingImage}
              />
              {uploadingImage && (
                <p style={{ color: '#3b82f6', marginTop: '0.5rem' }}>
                  Uploading image...
                </p>
              )}
              {formData.image_url && (
                <div className="image-preview-container">
                  <img
                    src={getFullImageUrl(formData.image_url, true)}
                    alt="Event preview"
                    className="image-preview"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = 'https://via.placeholder.com/400x200?text=Image+Error';
                    }}
                  />
                  <button
                    type="button"
                    onClick={handleRemoveImage}
                    className="btn-danger btn-sm"
                  >
                    Remove Image
                  </button>
                </div>
              )}
              <small className="help-text">
                Upload an image for the event (max 5MB). Recommended: 800x400px
              </small>
            </div>

         

            <div className="form-actions" style={{display:"flex", gap: '1rem' }}>
              <button type="submit" className="btn-primary" disabled={uploadingImage}>
                {editingEvent ? 'Update Event' : 'Create Event'}
              </button>
              <button type="button" onClick={handleCancel} className="btn-secondary">
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Regular Events Table */}
      <h3 style={{ marginBottom: '1rem', color: '#374151' }}>Regular Rounds</h3>
      <div className="events-table">
        <table>
          <thead>
            <tr>
              <th>Golf Course</th>
              <th>Location</th>
              <th>Date</th>
              <th>Time</th>
              <th>Capacity</th>
              <th>Registered</th>
              <th>Registration</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {events
              .filter(e => !e.event_type || e.event_type === 'regular')
              .sort((a, b) => new Date(a.date) - new Date(b.date))
              .map((event) => (
              <tr key={event.id}>
                <td>
                  <div className="event-cell-with-image">
                    {event.image_url && (
                      <img
                        src={getFullImageUrl(event.image_url)}
                        alt={event.golf_course}
                        className="event-thumbnail"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    )}
                    <strong>{event.golf_course}</strong>
                  </div>
                </td>
                <td>{event.township}, {event.state}</td>
                <td>{new Date(event.date).toLocaleDateString()}</td>
                <td>{formatTime(event.start_time)}</td>
                <td>{event.capacity}</td>
                <td>{event.registered || 0}</td>
                <td>
                  <span className={`reg-status-badge ${event.registration_open !== false ? 'reg-open' : 'reg-closed'}`}>
                    {event.registration_open !== false ? 'Open' : 'Closed'}
                  </span>
                </td>
                <td>
                  <div className="action-buttons">
                    <button onClick={() => handleEdit(event)} className="btn-secondary btn-sm">Edit</button>
                    <button
                      onClick={() => handleToggleRegistration(event)}
                      className={`btn-sm ${event.registration_open !== false ? 'btn-warning' : 'btn-success'}`}
                    >
                      {event.registration_open !== false ? 'Disable' : 'Enable'}
                    </button>
                    <button onClick={() => handleDelete(event.id, event.golf_course)} className="btn-danger btn-sm">Delete</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* SAGA Ryder Cup Section */}
      {(() => {
        const ryderEvent = events.find(e => e.event_type === 'ryder_cup');
        const ryderYear = ryderEvent ? new Date(ryderEvent.date).getFullYear() : new Date().getFullYear();
        const ryderEndDate = ryderEvent ? (() => { const d = new Date(ryderEvent.date); d.setDate(d.getDate() + 1); return d; })() : null;
        return (
          <div className="ryder-cup-section">
            <div className="section-header" style={{ marginTop: '2.5rem' }}>
              <h3 className="ryder-cup-section-title">SAGA Ryder Cup</h3>
              {!ryderEvent && !showForm && (
                <button onClick={() => { setFormData(prev => ({ ...prev, event_type: 'ryder_cup' })); setShowForm(true); setTimeout(() => formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100); }} className="btn-primary btn-sm">
                  + Add Ryder Cup Event
                </button>
              )}
            </div>
            {ryderEvent ? (
              <div className="ryder-cup-admin-card">
                <div className="ryder-cup-admin-header">
                  <div className="ryder-cup-admin-badge">🏆 SAGA Ryder Cup {ryderYear}</div>
                </div>
                <div className="events-table" style={{ boxShadow: 'none' }}>
                  <table>
                    <thead>
                      <tr>
                        <th>Golf Course</th>
                        <th>Location</th>
                        <th>Dates</th>
                        <th>Time</th>
                        <th>Capacity</th>
                        <th>Registered</th>
                        <th>Registration</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>
                          <div className="event-cell-with-image">
                            {ryderEvent.image_url && (
                              <img src={getFullImageUrl(ryderEvent.image_url)} alt={ryderEvent.golf_course} className="event-thumbnail" onError={(e) => { e.target.style.display = 'none'; }} />
                            )}
                            <strong>{ryderEvent.golf_course}</strong>
                          </div>
                        </td>
                        <td>{ryderEvent.township}, {ryderEvent.state}</td>
                        <td>{new Date(ryderEvent.date).toLocaleDateString()} – {ryderEndDate.toLocaleDateString()}</td>
                        <td>{formatTime(ryderEvent.start_time)}</td>
                        <td>{ryderEvent.capacity}</td>
                        <td>{ryderEvent.registered || 0}</td>
                        <td>
                          <span className={`reg-status-badge ${ryderEvent.registration_open !== false ? 'reg-open' : 'reg-closed'}`}>
                            {ryderEvent.registration_open !== false ? 'Open' : 'Closed'}
                          </span>
                        </td>
                        <td>
                          <div className="action-buttons">
                            <button onClick={() => handleEdit(ryderEvent)} className="btn-secondary btn-sm">Edit</button>
                            <button onClick={() => handleToggleRegistration(ryderEvent)} className={`btn-sm ${ryderEvent.registration_open !== false ? 'btn-warning' : 'btn-success'}`}>
                              {ryderEvent.registration_open !== false ? 'Disable' : 'Enable'}
                            </button>
                            <button onClick={() => handleDelete(ryderEvent.id, ryderEvent.golf_course)} className="btn-danger btn-sm">Delete</button>
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div style={{ color: '#9ca3af', textAlign: 'center', padding: '2rem', background: '#f9fafb', borderRadius: '8px', border: '1px dashed #e5e7eb' }}>
                No Ryder Cup event created yet.
              </div>
            )}
          </div>
        );
      })()}

      {/* Championship Event Section */}
      {(() => {
        const champEvent = events.find(e => e.event_type === 'championship');
        if (!champEvent) return (
          <div className="championship-section">
            <div className="section-header" style={{ marginTop: '2.5rem' }}>
              <h3 className="championship-section-title">Championship Event</h3>
              {!showForm && (
                <button onClick={() => { setFormData(prev => ({ ...prev, event_type: 'championship' })); setShowForm(true); setTimeout(() => formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100); }} className="btn-primary btn-sm">
                  + Add Championship Event
                </button>
              )}
            </div>
            <div style={{ color: '#9ca3af', textAlign: 'center', padding: '2rem', background: '#f9fafb', borderRadius: '8px', border: '1px dashed #e5e7eb' }}>
              No championship event created yet.
            </div>
          </div>
        );
        const champYear = new Date(champEvent.date).getFullYear();
        return (
          <div className="championship-section">
            <h3 className="championship-section-title">Championship Event</h3>
            <div className="championship-admin-card">
              <div className="championship-admin-header">
                <div className="championship-admin-badge">🏆 SAGA Open {champYear}</div>
              </div>
              <div className="events-table" style={{ boxShadow: 'none' }}>
                <table>
                  <thead>
                    <tr>
                      <th>Golf Course</th>
                      <th>Location</th>
                      <th>Date</th>
                      <th>Time</th>
                      <th>Capacity</th>
                      <th>Registered</th>
                      <th>Registration</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>
                        <div className="event-cell-with-image">
                          {champEvent.image_url && (
                            <img src={getFullImageUrl(champEvent.image_url)} alt={champEvent.golf_course} className="event-thumbnail" onError={(e) => { e.target.style.display = 'none'; }} />
                          )}
                          <strong>{champEvent.golf_course}</strong>
                        </div>
                      </td>
                      <td>{champEvent.township}, {champEvent.state}</td>
                      <td>{new Date(champEvent.date).toLocaleDateString()}</td>
                      <td>{formatTime(champEvent.start_time)}</td>
                      <td>{champEvent.capacity}</td>
                      <td>{champEvent.registered || 0}</td>
                      <td>
                        <span className={`reg-status-badge ${champEvent.registration_open !== false ? 'reg-open' : 'reg-closed'}`}>
                          {champEvent.registration_open !== false ? 'Open' : 'Closed'}
                        </span>
                      </td>
                      <td>
                        <div className="action-buttons">
                          <button onClick={() => handleEdit(champEvent)} className="btn-secondary btn-sm">Edit</button>
                          <button onClick={() => handleToggleRegistration(champEvent)} className={`btn-sm ${champEvent.registration_open !== false ? 'btn-warning' : 'btn-success'}`}>
                            {champEvent.registration_open !== false ? 'Disable' : 'Enable'}
                          </button>
                          <button onClick={() => handleDelete(champEvent.id, champEvent.golf_course)} className="btn-danger btn-sm">Delete</button>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Promo Codes Section */}
      <div className="promo-codes-section">
        <div className="section-header" style={{ marginTop: '2.5rem' }}>
          <h3 style={{ color: '#374151', margin: 0 }}>Event Promo Codes</h3>
          {!showPromoForm && (
            <button onClick={() => setShowPromoForm(true)} className="btn-primary btn-sm">
              + Create Promo Code
            </button>
          )}
        </div>

        {showPromoForm && (
          <div className="event-form-card">
            <h3>Create Promo Code</h3>
            <form onSubmit={handleCreatePromoCode} className="admin-form">
              <div className="form-row">
                <div className="form-group">
                  <label>Code (leave blank to auto-generate)</label>
                  <input
                    type="text"
                    value={promoFormData.code}
                    onChange={(e) => setPromoFormData({ ...promoFormData, code: e.target.value.toUpperCase() })}
                    placeholder="e.g., GUEST2026"
                  />
                </div>
                <div className="form-group">
                  <label>Discount Type *</label>
                  <select
                    value={promoFormData.discount_type}
                    onChange={(e) => setPromoFormData({ ...promoFormData, discount_type: e.target.value, discount_value: '' })}
                    required
                  >
                    <option value="member_price">Give Member Price</option>
                    <option value="free">Make Event Free</option>
                    <option value="percent">Percentage Discount</option>
                  </select>
                </div>
              </div>

              {promoFormData.discount_type === 'percent' && (
                <div className="form-group">
                  <label>Discount Percentage *</label>
                  <input
                    type="number"
                    value={promoFormData.discount_value}
                    onChange={(e) => setPromoFormData({ ...promoFormData, discount_value: e.target.value })}
                    required
                    min="1"
                    max="100"
                    placeholder="e.g., 25"
                  />
                </div>
              )}

              <div className="form-row">
                <div className="form-group">
                  <label>Specific Event (optional)</label>
                  <select
                    value={promoFormData.event_id}
                    onChange={(e) => setPromoFormData({ ...promoFormData, event_id: e.target.value })}
                  >
                    <option value="">All Events</option>
                    {events.map(ev => (
                      <option key={ev.id} value={ev.id}>
                        {ev.golf_course} — {new Date(ev.date).toLocaleDateString()}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Max Uses *</label>
                  <input
                    type="number"
                    value={promoFormData.max_uses}
                    onChange={(e) => setPromoFormData({ ...promoFormData, max_uses: e.target.value })}
                    required
                    min="1"
                    placeholder="1"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Expires At (optional)</label>
                <input
                  type="date"
                  value={promoFormData.expires_at}
                  onChange={(e) => setPromoFormData({ ...promoFormData, expires_at: e.target.value })}
                />
              </div>

              <div className="form-actions" style={{ display: 'flex', gap: '1rem' }}>
                <button type="submit" className="btn-primary">Create Promo Code</button>
                <button type="button" onClick={() => { setShowPromoForm(false); setPromoFormData({ code: '', discount_type: 'member_price', discount_value: '', event_id: '', max_uses: 1, expires_at: '' }); }} className="btn-secondary">
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {promoCodes.length > 0 ? (
          <div className="events-table">
            <table>
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Type</th>
                  <th>Value</th>
                  <th>Event</th>
                  <th>Uses</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {promoCodes.map((pc) => (
                  <tr key={pc.id}>
                    <td><code className="code-value">{pc.code}</code></td>
                    <td>
                      {pc.discount_type === 'member_price' && 'Member Price'}
                      {pc.discount_type === 'free' && 'Free'}
                      {pc.discount_type === 'percent' && 'Percentage'}
                    </td>
                    <td>
                      {pc.discount_type === 'percent' ? `${pc.discount_value}%` : '—'}
                    </td>
                    <td>
                      {pc.event_id
                        ? (events.find(e => e.id === pc.event_id)?.golf_course || `Event #${pc.event_id}`)
                        : 'All Events'}
                    </td>
                    <td>{pc.times_used} / {pc.max_uses}</td>
                    <td>
                      <span className={`reg-status-badge ${pc.is_active ? 'reg-open' : 'reg-closed'}`}>
                        {pc.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button onClick={() => { navigator.clipboard.writeText(pc.code); }} className="btn-secondary btn-sm">Copy</button>
                        <button onClick={() => handleDeletePromoCode(pc.id)} className="btn-danger btn-sm">Delete</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div style={{ color: '#9ca3af', textAlign: 'center', padding: '2rem', background: '#f9fafb', borderRadius: '8px', border: '1px dashed #e5e7eb' }}>
            No promo codes created yet.
          </div>
        )}
      </div>

      <style jsx>{`
        .section-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1.5rem;
          flex-wrap: wrap;
          gap: 1rem;
        }

        .header-actions {
          display: flex;
          gap: 0.75rem;
          align-items: center;
        }

        .event-form-card {
          background: #f9fafb;
          padding: 1.5rem;
          border-radius: 8px;
          margin-bottom: 2rem;
          border: 1px solid #e5e7eb;
        }

        .event-form-card h3 {
          margin: 0 0 1.5rem 0;
        }

        .form-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 1rem;
        }

        .image-preview-container {
          margin-top: 0.75rem;
          padding: 1rem;
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 6px;
        }

        .image-preview {
          max-width: 400px;
          max-height: 200px;
          object-fit: cover;
          border-radius: 4px;
          display: block;
          margin-bottom: 0.75rem;
        }

        .events-table {
          background: white;
          border-radius: 8px;
          overflow: hidden;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }

        .events-table table {
          width: 100%;
          border-collapse: collapse;
        }

        .events-table th {
          background: #f9fafb;
          padding: 0.75rem 1rem;
          text-align: left;
          font-weight: 600;
          color: #374151;
          border-bottom: 1px solid #e5e7eb;
        }

        .events-table td {
          padding: 0.75rem 1rem;
          border-bottom: 1px solid #f3f4f6;
        }

        .events-table tr:hover {
          background: #f9fafb;
        }

        .event-cell-with-image {
          display: flex;
          align-items: center;
          gap: 0.75rem;
        }

        .event-thumbnail {
          width: 60px;
          height: 40px;
          object-fit: contain;
          border-radius: 4px;
          border: 1px solid #e5e7eb;
        }

        .action-buttons {
          display: flex;
          gap: 0.5rem;
        }

        .btn-sm {
          padding: 0.4rem 0.8rem;
          font-size: 0.85rem;
        }

        .btn-warning {
          background: #f59e0b;
          color: #fff;
          border: none;
          border-radius: 6px;
          cursor: pointer;
          font-weight: 500;
          transition: background 0.2s;
        }
        .btn-warning:hover { background: #d97706; }

        .btn-success {
          background: #10b981;
          color: #fff;
          border: none;
          border-radius: 6px;
          cursor: pointer;
          font-weight: 500;
          transition: background 0.2s;
        }
        .btn-success:hover { background: #059669; }

        .reg-status-badge {
          display: inline-block;
          padding: 0.2rem 0.6rem;
          border-radius: 999px;
          font-size: 0.78rem;
          font-weight: 600;
        }
        .reg-open  { background: #d1fae5; color: #065f46; }
        .reg-closed { background: #fee2e2; color: #991b1b; }

        .ryder-cup-section {
          margin-top: 0;
        }

        .ryder-cup-section-title {
          color: #374151;
          margin: 0;
        }

        .ryder-cup-admin-card {
          border: 2px solid;
          border-image: linear-gradient(135deg, #dc2626, #2563eb) 1;
          border-radius: 0;
          overflow: hidden;
          background: #fafbff;
        }

        .ryder-cup-admin-header {
          padding: 0.75rem 1rem;
          background: linear-gradient(135deg, #dc2626, #2563eb);
        }

        .ryder-cup-admin-badge {
          color: white;
          font-weight: 700;
          font-size: 1.1rem;
        }

        .championship-section {
          margin-top: 2.5rem;
        }

        .championship-section-title {
          color: #374151;
          margin-bottom: 1rem;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .championship-admin-card {
          border: 2px solid #f59e0b;
          border-radius: 8px;
          overflow: hidden;
          background: #fffbeb;
        }

        .championship-admin-header {
          padding: 0.75rem 1rem;
          background: linear-gradient(135deg, #f59e0b, #d97706);
        }

        .championship-admin-badge {
          color: white;
          font-weight: 700;
          font-size: 1.1rem;
        }

        .promo-codes-section {
          margin-top: 0;
        }

        .code-value {
          background: #f1f5f9;
          padding: 0.2rem 0.5rem;
          border-radius: 4px;
          font-family: monospace;
          font-size: 0.95rem;
          letter-spacing: 0.5px;
        }

@media (max-width: 768px) {
          .form-row {
            grid-template-columns: 1fr;
          }

          .header-actions {
            width: 100%;
            flex-direction: column;
          }

          .header-actions button {
            width: 100%;
          }

          .events-table {
            overflow-x: auto;
          }

          .events-table table {
            min-width: 800px;
          }
        }
      `}</style>
    </div>
  );
};

export default EventManagement;
